/**
 * SettingsScreen for Poker Reborn
 *
 * Player settings screen with tabbed layout:
 * - Sound tab: Volume sliders and mute toggle
 * - Graphics tab: Quality selector
 * - Play tab: Gameplay toggles
 * - Account tab: Social linking + logout
 *
 * Settings are persisted via StorageService.
 *
 * @see docs/screens/settings/README.md - Feature spec, component hierarchy, tab definitions
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, DANGER_BUTTON_STYLE } from '@/components/common/Button';
import { Slider } from '@/components/common/Slider';
import { Toggle } from '@/components/common/Toggle';
import { TabBar } from '@/components/common/TabBar';
import { SettingsBar } from '@/components/settings/SettingsBar';
import { storageService } from '@/services/StorageService';
import { audioService } from '@/services/AudioService';
import { authService } from '@/services/AuthService';
import { navigateToScreen, setAccountIdentity, appState } from '@/state/AppState';
import type { AuthCredentials } from '@/services/AuthService';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Background color */
  background: 0x1a1a2e,

  /** Header */
  header: {
    height: 60,
    titleColor: 0xffffff,
    titleSize: 28,
    padding: 20,
  },

  /** Tab bar */
  tabBar: {
    height: 48,
    maxWidth: 400,
    marginTop: 10,
  },

  /** Panel styling */
  panel: {
    backgroundColor: 0x252538,
    borderRadius: 12,
    padding: 24,
    maxWidth: 500,
    itemGap: 24,
  },

  /** Quality button */
  qualityButton: {
    width: 100,
    height: 40,
    gap: 12,
  },
};

// =============================================================================
// TAB IDS
// =============================================================================

type TabId = 'sound' | 'graphics' | 'gameplay' | 'account';

const TABS: Array<{ id: TabId; icon: string; label: string }> = [
  { id: 'sound', icon: '🔊', label: 'Sound' },
  { id: 'graphics', icon: '🎨', label: 'Graphics' },
  { id: 'gameplay', icon: '🎮', label: 'Play' },
  { id: 'account', icon: '👤', label: 'Account' },
];

// =============================================================================
// SETTINGS SCREEN
// =============================================================================

export class SettingsScreen extends Container {
  private screenWidth: number;
  private screenHeight: number;

  // Background
  private background: Graphics;

  // Settings bar (title + close button)
  private settingsBar: SettingsBar;

  // Tab bar
  private tabBar: TabBar;

  // Panels
  private soundPanel: Container;
  private graphicsPanel: Container;
  private gameplayPanel: Container;
  private accountPanel: Container;
  private panels: Map<TabId, Container> = new Map();

  // Sound controls
  private masterSlider: Slider;
  private soundEnabledToggle: Toggle;

  // Graphics controls
  private qualityButtons: Button[] = [];
  private currentQuality: 'low' | 'medium' | 'high';

  // Gameplay controls
  private autoMuckToggle: Toggle;
  private fourColorToggle: Toggle;
  private handStrengthToggle: Toggle;

  // Account controls
  private logoutButton: Button;
  private onAuthenticated: (credentials: AuthCredentials) => void;

  constructor(width: number, height: number) {
    super();

    this.screenWidth = width;
    this.screenHeight = height;

    // Load current settings
    const soundSettings = storageService.getSoundSettings();
    const gameSettings = storageService.getGameSettings();
    this.currentQuality = storageService.getOrDefault(
      'settings.graphics.quality',
      'high'
    );

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // =========================================================================
    // SETTINGS BAR (title + close button)
    // =========================================================================

    this.settingsBar = new SettingsBar(width, height);
    this.settingsBar.onClose = () => { navigateToScreen('lobby'); };
    this.addChild(this.settingsBar);

    // =========================================================================
    // TAB BAR
    // =========================================================================

    const tabBarWidth = Math.min(STYLES.tabBar.maxWidth, width - 40);
    this.tabBar = new TabBar({
      tabs: TABS,
      width: tabBarWidth,
      height: STYLES.tabBar.height,
      activeTab: 'sound',
    });
    this.tabBar.onTabChange = (tabId) => { this.showPanel(tabId as TabId); };
    this.addChild(this.tabBar);

    // =========================================================================
    // SOUND PANEL
    // =========================================================================

    this.soundPanel = this.createPanelContainer();
    this.panels.set('sound', this.soundPanel);
    this.addChild(this.soundPanel);

    // Master volume slider
    this.masterSlider = new Slider({
      width: STYLES.panel.maxWidth - STYLES.panel.padding * 2,
      label: 'Master Volume',
      value: soundSettings.master,
      min: 0,
      max: 1,
      step: 0.01,
    });
    this.masterSlider.onChange = (value) => {
      storageService.set('settings.sound.master', value);
      audioService.setMasterVolume(value);
    };
    this.soundPanel.addChild(this.masterSlider);

    // Sound enabled toggle (inverted from muted)
    this.soundEnabledToggle = new Toggle({
      label: 'Enable Sound',
      value: !soundSettings.muted,
    });
    this.soundEnabledToggle.onChange = (value) => {
      const muted = !value;
      storageService.set('settings.sound.muted', muted);
      audioService.setMuted(muted);
    };
    this.soundPanel.addChild(this.soundEnabledToggle);

    // =========================================================================
    // GRAPHICS PANEL
    // =========================================================================

    this.graphicsPanel = this.createPanelContainer();
    this.graphicsPanel.visible = false;
    this.panels.set('graphics', this.graphicsPanel);
    this.addChild(this.graphicsPanel);

    // Quality label
    const qualityLabel = new Text({
      text: 'Quality',
      style: {
        fill: 0xcccccc,
        fontSize: 16,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.graphicsPanel.addChild(qualityLabel);

    // Quality buttons container
    const qualityContainer = new Container();
    this.graphicsPanel.addChild(qualityContainer);

    const qualities: Array<'low' | 'medium' | 'high'> = [
      'low',
      'medium',
      'high',
    ];
    qualities.forEach((quality, index) => {
      const isSelected = quality === this.currentQuality;
      const button = new Button({
        label: quality.charAt(0).toUpperCase() + quality.slice(1),
        width: STYLES.qualityButton.width,
        height: STYLES.qualityButton.height,
        style: this.getQualityButtonStyle(isSelected),
      });
      button.x = index * (STYLES.qualityButton.width + STYLES.qualityButton.gap);
      button.onClick = () => { this.setQuality(quality); };
      qualityContainer.addChild(button);
      this.qualityButtons.push(button);
    });

    // =========================================================================
    // GAMEPLAY PANEL
    // =========================================================================

    this.gameplayPanel = this.createPanelContainer();
    this.gameplayPanel.visible = false;
    this.panels.set('gameplay', this.gameplayPanel);
    this.addChild(this.gameplayPanel);

    // Auto muck toggle
    this.autoMuckToggle = new Toggle({
      label: 'Auto Muck Losing Hands',
      value: gameSettings.autoMuck,
    });
    this.autoMuckToggle.onChange = (value) => {
      storageService.set('settings.game.autoMuck', value);
    };
    this.gameplayPanel.addChild(this.autoMuckToggle);

    // Four color deck toggle
    this.fourColorToggle = new Toggle({
      label: 'Four Color Deck',
      value: gameSettings.fourColorDeck,
    });
    this.fourColorToggle.onChange = (value) => {
      storageService.set('settings.game.fourColorDeck', value);
    };
    this.gameplayPanel.addChild(this.fourColorToggle);

    // Hand strength toggle
    this.handStrengthToggle = new Toggle({
      label: 'Show Hand Strength',
      value: gameSettings.showHandStrength,
    });
    this.handStrengthToggle.onChange = (value) => {
      storageService.set('settings.game.showHandStrength', value);
    };
    this.gameplayPanel.addChild(this.handStrengthToggle);

    // =========================================================================
    // ACCOUNT PANEL
    // =========================================================================

    this.accountPanel = this.createPanelContainer();
    this.accountPanel.visible = false;
    this.panels.set('account', this.accountPanel);
    this.addChild(this.accountPanel);

    this.logoutButton = new Button({
      label: 'Logout',
      width: 200,
      height: 44,
      style: DANGER_BUTTON_STYLE,
    });
    this.logoutButton.onClick = () => { this.handleLogout(); };

    this.buildAccountPanel();

    // Listen for auth changes to rebuild account panel
    this.onAuthenticated = (credentials: AuthCredentials) => {
      const provider = credentials.provider;
      const currentProviders = appState.linkedProviders.get();
      if (!currentProviders.includes(provider)) {
        setAccountIdentity(false, [...currentProviders, provider]);
      }
      this.buildAccountPanel();
      this.layout();
    };
    authService.on('authenticated', this.onAuthenticated);

    // Initial layout
    this.layout();
  }

  /**
   * Resize handler
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.settingsBar.resize(width, height);
    this.layout();
  }

  /**
   * Refresh all settings from storage/services
   * Called when screen is shown to ensure UI reflects current state
   */
  refresh(): void {
    // Refresh sound settings
    const isMuted = audioService.isMuted();
    this.soundEnabledToggle.value = !isMuted;
    this.masterSlider.value = storageService.getSoundSettings().master;

    // Refresh graphics settings
    const quality = storageService.getOrDefault(
      'settings.graphics.quality',
      'high'
    );
    if (quality !== this.currentQuality) {
      this.setQuality(quality);
    }

    // Refresh gameplay settings
    const gameSettings = storageService.getGameSettings();
    this.autoMuckToggle.value = gameSettings.autoMuck;
    this.fourColorToggle.value = gameSettings.fourColorDeck;
    this.handStrengthToggle.value = gameSettings.showHandStrength;

    // Refresh account panel (guest status may have changed)
    this.buildAccountPanel();
  }

  /**
   * Clean up event listeners
   */
  override destroy(options?: boolean | { children?: boolean }): void {
    authService.off('authenticated', this.onAuthenticated);
    super.destroy(options);
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private createPanelContainer(): Container {
    const panel = new Container();

    // Panel background (drawn in layout)
    const background = new Graphics();
    background.label = 'panelBg';
    panel.addChild(background);

    return panel;
  }

  private showPanel(tabId: TabId): void {
    // Hide all panels
    this.panels.forEach((panel, id) => {
      panel.visible = id === tabId;
    });

    // Re-layout to reposition logout button below the new active panel
    this.layout();
  }

  private getQualityButtonStyle(isSelected: boolean) {
    return {
      backgroundColor: isSelected ? 0x4a8a44 : 0x333344,
      hoverColor: isSelected ? 0x5a9a54 : 0x444455,
      pressColor: isSelected ? 0x3a7a34 : 0x222233,
      borderColor: isSelected ? 0x6aba64 : 0x444455,
    };
  }

  private setQuality(quality: 'low' | 'medium' | 'high'): void {
    this.currentQuality = quality;
    storageService.set('settings.graphics.quality', quality);

    // Update button styles
    const qualities: Array<'low' | 'medium' | 'high'> = [
      'low',
      'medium',
      'high',
    ];
    this.qualityButtons.forEach((button, index) => {
      const isSelected = qualities[index] === quality;
      button.setStyle(this.getQualityButtonStyle(isSelected));
    });
  }

  private buildAccountPanel(): void {
    // Remove all children except the panel background and the persistent logout button
    const panelChildren = [...this.accountPanel.children];
    for (const child of panelChildren) {
      if (child.label === 'panelBg' || child === this.logoutButton) continue;
      this.accountPanel.removeChild(child);
      child.destroy({ children: true });
    }
    // Detach logout button (will be re-added at the end)
    if (this.logoutButton.parent === this.accountPanel) {
      this.accountPanel.removeChild(this.logoutButton);
    }

    const isGuest = appState.isGuest.get();
    const linkedProviders = appState.linkedProviders.get();
    const panelContentWidth = STYLES.panel.maxWidth - STYLES.panel.padding * 2;

    if (isGuest) {
      // Guest account — show link buttons
      const linkLabel = new Text({
        text: 'Link your account:',
        style: {
          fill: 0xcccccc,
          fontSize: 16,
          fontFamily: 'Roboto, Arial, sans-serif',
        },
      });
      this.accountPanel.addChild(linkLabel);

      const googleButton = new Button({
        label: 'Link Google',
        width: panelContentWidth,
        height: 44,
        style: {
          backgroundColor: 0x4285f4,
          hoverColor: 0x5a95f5,
          pressColor: 0x3275e4,
          borderColor: 0x4285f4,
        },
      });
      googleButton.onClick = () => { void authService.googleLogin(); };
      this.accountPanel.addChild(googleButton);

      const facebookButton = new Button({
        label: 'Link Facebook',
        width: panelContentWidth,
        height: 44,
        style: {
          backgroundColor: 0x1877f2,
          hoverColor: 0x2887f3,
          pressColor: 0x0867e2,
          borderColor: 0x1877f2,
        },
      });
      facebookButton.onClick = () => { void authService.facebookLogin(); };
      this.accountPanel.addChild(facebookButton);

      const appleButton = new Button({
        label: 'Link Apple',
        width: panelContentWidth,
        height: 44,
        style: {
          backgroundColor: 0x000000,
          hoverColor: 0x222222,
          pressColor: 0x111111,
          borderColor: 0x333333,
        },
      });
      appleButton.onClick = () => { void authService.appleLogin(); };
      this.accountPanel.addChild(appleButton);
    } else {
      // Linked account — show status
      const providersText = linkedProviders.length > 0
        ? linkedProviders.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')
        : 'Email';
      const statusLabel = new Text({
        text: `Linked: ${providersText}`,
        style: {
          fill: 0x88cc88,
          fontSize: 16,
          fontFamily: 'Roboto, Arial, sans-serif',
        },
      });
      this.accountPanel.addChild(statusLabel);
    }

    // Logout button (always present in account panel)
    this.accountPanel.addChild(this.logoutButton);
  }

  private handleLogout(): void {
    authService.clearCredentials();
    window.location.reload();
  }

  private layout(): void {
    const { screenWidth: w, screenHeight: h } = this;
    const padding = STYLES.header.padding;

    // Draw background
    this.background.clear();
    this.background.rect(0, 0, w, h);
    this.background.fill(STYLES.background);

    // Content starts below settings bar
    const barHeight = this.settingsBar.getBarHeight();

    // Position tab bar (centered, below settings bar)
    const tabBarWidth = Math.min(STYLES.tabBar.maxWidth, w - 40);
    this.tabBar.setWidth(tabBarWidth);
    this.tabBar.x = (w - tabBarWidth) / 2;
    this.tabBar.y = barHeight + STYLES.tabBar.marginTop;

    // Calculate panel area
    const panelTop = this.tabBar.y + STYLES.tabBar.height + 20;
    const panelWidth = Math.min(STYLES.panel.maxWidth, w - padding * 2);
    const panelX = (w - panelWidth) / 2;

    // Layout each panel
    this.panels.forEach((panel) => {
      panel.x = panelX;
      panel.y = panelTop;
      this.layoutPanel(panel, panelWidth);
    });
  }

  private layoutPanel(panel: Container, width: number): void {
    const padding = STYLES.panel.padding;
    const itemGap = STYLES.panel.itemGap;

    // Position items
    let itemY = padding;

    panel.children.forEach((child) => {
      if (child.label === 'panelBg') return;

      child.x = padding;
      child.y = itemY;
      itemY += 50 + itemGap; // Approximate item height + gap
    });

    const totalHeight = itemY + padding - itemGap;

    // Draw panel background
    const background = panel.getChildByLabel('panelBg') as Graphics;
    background.clear();
    background.roundRect(0, 0, width, totalHeight, STYLES.panel.borderRadius);
    background.fill(STYLES.panel.backgroundColor);
  }
}
